'use client';

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { milestonesFor } from '@/components/quiz/momentum';
import { playMilestoneSlam } from '@/lib/utils/quizSounds';

/**
 * How long a slam takes. The stylesheet reads it from `--sf-slam-ms`, so a slam
 * timed in JS and one animated in CSS are the same length by construction.
 */
export const SLAM_MS = 620;
export const slamDuration = { '--sf-slam-ms': `${SLAM_MS}ms` } as React.CSSProperties;

/**
 * The full-card word a quiz throws up mid-run — "LET'S GO", "IN THE ZONE",
 * "ONE MORE" — placed by `milestonesFor` so a short quiz gets one and a long
 * one gets all three.
 *
 * It is not decoration: the last of them fires with a single question left,
 * where quitting costs the player everything they have already answered. A
 * quiz that plays straight through without them is a form, not a game.
 */
export function useQuizSlam(total: number) {
  const [slam, setSlam] = useState<{ text: string; key: number } | null>(null);
  const shown = useRef<Set<number>>(new Set());

  /** Call with the index of the question just answered. */
  const fire = useCallback(
    (answeredIndex: number) => {
      const milestone = milestonesFor(total).find(
        (candidate) =>
          candidate.afterIndex === answeredIndex && !shown.current.has(candidate.afterIndex)
      );
      if (!milestone) return;
      shown.current.add(milestone.afterIndex);
      try {
        playMilestoneSlam();
      } catch {
        // a silent slam is still a slam
      }
      setSlam({ text: milestone.text, key: milestone.afterIndex });
    },
    [total]
  );

  const reset = useCallback(() => {
    shown.current.clear();
    setSlam(null);
  }, []);

  return { slam, fire, reset, clear: () => setSlam(null) };
}

/** The overlay itself. Render it inside the card, above everything. */
export function QuizSlam({
  slam,
  onDone,
  children,
}: {
  slam: { text: string; key: number } | null;
  onDone: () => void;
  /** The word, wrapped by the caller when it needs its own treatment. */
  children?: ReactNode;
}) {
  if (!slam) return null;
  return (
    <div
      key={slam.key}
      aria-hidden
      onAnimationEnd={(event) => {
        // The word inside runs its own animation; only the box's own end means done.
        if (event.target === event.currentTarget) onDone();
      }}
      style={slamDuration}
      className="sf-slam"
    >
      {children ?? <span className="sf-slam-word">{slam.text}</span>}
    </div>
  );
}
